import {Injectable} from '@angular/core';
import {AuthState, LoginData, LoginResponse, User, UserRole} from "./auth.model";
import {Observable} from "rxjs/internal/Observable";
import {HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {filter, map, mergeMap, tap} from "rxjs/operators";
import {EMPTY} from "rxjs/internal/observable/empty";
import * as Cookies from "js-cookie";
import {CommonHttpService, optimistic} from "../api/common-http.service";
import {AppResponse} from "../api/app-response.model";
import {Router} from "@angular/router";
import {NEVER, of} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class AuthManagerService {

  constructor(
    private http: CommonHttpService,
    private router: Router
  ) {
  }

  private readonly cookieName = "AuthToken";

  currentUser: User;
  token: string;
  isLoggedIn: boolean = false;
  isRecovering: boolean = false;
  isSigningIn: boolean = false;

  checkAndApplyToken(token: string): Observable<boolean> {
    return this.authRequest<User>("GET", "/current-user", null, null, token,
      error => of(AppResponse.failure<User>(error))).pipe(
      tap(r => {
        if (r.success) {
          this.setLogin(token, r.body, undefined);
        }
      }),
      map(r => r.success)
    )
  }

  get currentUser$(): Observable<User> {
    return of(this.currentUser);
  }

  get isAccountPresent$(): Observable<boolean> {
    return this.currentUser$.pipe(
      map(u => !!u && !!u.account)
    );
  }

  get isAdmin$(): Observable<boolean> {
    return this.currentUser$.pipe(
      map(u => !!u && u.role === UserRole.ADMIN)
    );
  }

  private setLogin(token: string, user: User, rememberMe: boolean) {
    if (rememberMe) {
      Cookies.set(this.cookieName, token, {expires: rememberMe ? 31536000 : undefined});
    } else {
      Cookies.set(this.cookieName, token, {expires: undefined});
    }
    this.isLoggedIn = true;
    this.currentUser = user;
    this.token = token;
  }

  login(loginData: LoginData, isSuperAdmin: boolean = false): Observable<AppResponse<LoginResponse>> {
    return this.http.post<LoginResponse>("/login", loginData, {isSuperAdmin}).pipe(
      map(response => {
        if (response.success) {
          if (response.body.user && response.body.token) {
            this.setLogin(response.body.token, response.body.user, loginData.rememberMe);
            console.log(loginData.rememberMe);
          } else {
            this.purgeSession();
            throw new Error("No user or token received");
          }
        } else {
          this.purgeSession();
        }
        return response;
      })
    );
  }

  change(loginData: LoginData, customerName: string) {
    return this.authRequest<LoginResponse>("POST", "/change", {customerName}, loginData, Cookies.get(this.cookieName), () => undefined).pipe(
      map(response => {
        if (response.success) {
            this.setLogin(response.body.token, response.body.user, true);
        } else {
          throw new Error("No user or token received");
        }
        return response;
      })
    );
  }

  logout() {
    this.authRequest("POST", "/exit", null, null, Cookies.get(this.cookieName), () => undefined).subscribe();
    this.purgeSession();
  }

  private handleUnauthorized() {
    if (this.isLoggedIn || this.isRecovering) {
      console.log("session expired");
      this.router.navigateByUrl("/");
      this.purgeSession();
    }
  }

  private purgeSession() {
    Cookies.remove(this.cookieName);
    this.isLoggedIn = false;
    this.isRecovering = false;
    this.isSigningIn = false;
  }

  authRequest<T>(method: string, path: string, parameters?: any, body?: any,
                 token?: string, handleUnauthorized?: (error: HttpErrorResponse) => Observable<AppResponse<T>>): Observable<AppResponse<T>> {
    return this.authRequestDecorate(
      token => this.http.request<T>(method, path, parameters, body, this.getAuthTokenHeaders(token)),
      token, handleUnauthorized);
  }

  authRequestBlob(method: string, path: string, parameters?: any, body?: any,
                  token?: string, handleUnauthorized?: (error: HttpErrorResponse) => Observable<AppResponse<Blob>>): Observable<AppResponse<Blob>> {
    return this.authRequestDecorate(
      token => this.http.requestBlob(method, path, parameters, body, this.getAuthTokenHeaders(token)),
      token, handleUnauthorized);
  }

  private getAuthTokenHeaders(token: string) {
    return new HttpHeaders({
      "Authorization": "Token " + token
    });
  }

  private authRequestDecorate<T>(request: (token: string) => Observable<AppResponse<T>>,
                                 token: string = this.token,
                                 handleUnauthorized: (error: HttpErrorResponse) => void = () => this.handleUnauthorized()): Observable<any> {
    if (token) {
      return request(token).pipe(
        mergeMap(response => {
          if (!response.success && response.httpError.status === 401) {
            handleUnauthorized(response.httpError);
            return NEVER;
          } else {
            return of(response);
          }
        })
      );
    } else {
      this.handleUnauthorized();
      return EMPTY;
    }
  }
}
